import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CheckoutStepService } from '@spartacus/checkout/base/components';
import { GenActiveCartService } from '../../cart/core/facade/active-cart.service';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { LaunchDialogService } from '@spartacus/storefront';
import { GlobalMessageService, GlobalMessageType } from '@spartacus/core';
import { GenLoadingEnum } from 'src/app/generac/enums/loading.enum';
import { GenShippingAccountType } from 'src/app/generac/enums/shipping-account-type.enum';
import { GenShippingAccount, GenShippingGroup } from 'src/app/generac/models/cart.model';
import { GenCheckoutService } from 'src/app/generac/services/checkout.service';
import { GenCartType } from 'src/app/generac/enums/cart-type.enum';

@Component({
  selector: 'gen-checkout-service-parts',
  templateUrl: './checkout-service-parts.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class GenCheckoutServicePartsComponent implements OnDestroy {
  private updateDeliveryMethodsSubject$$ = new BehaviorSubject<GenShippingAccountType | null>(null);
  private destroy$ = new Subject();

  cartCode: string;
  daFreeShipping: boolean;
  purchaseOrderNumber: string;
  userId: string;
  shippingGroupName: string;
  updateDeliveryMethodsTrigger$ = this.updateDeliveryMethodsSubject$$.asObservable();
  generacShippingAccounts: GenShippingAccount[];
  dealerShippingAccounts: GenShippingAccount[];
  carrierSelected: GenShippingAccount;
  selectedCarrier: string;
  selectedShippingCondition: string;
  selectedShippingAccount: GenShippingAccountType = GenShippingAccountType.GENERAC;
  isLtlItemExists: boolean;
  loading = GenLoadingEnum.Idle;
  GenLoadingEnum = GenLoadingEnum;
  skipPaymentTypeStep: boolean = false;

  shippingGroup$: Observable<GenShippingGroup> = this.activeCartFacade.getActive().pipe(
    tap(cart => {
      this.checkoutService.disableCheckoutSteps(cart?.cartTypes, cart?.engineReplacement);
      this.cartCode = cart.code;
      this.daFreeShipping = cart?.daFreeShipping;
      this.userId = cart.user?.uid;
      this.purchaseOrderNumber = cart.purchaseOrderNumber;
      this.skipPaymentTypeStep = cart?.wellsFargoCart;
    }),
    switchMap(cart => of(cart.shippingGroups) as Observable<GenShippingGroup[]>),
    map((shippingGroups: GenShippingGroup[]) => shippingGroups.filter(sg => sg.cartTypes[0] === GenCartType.SERVICE_PARTS.toString())),
    switchMap(shippingGroups => of(shippingGroups[0])),
    tap(shippingGroup => this.setShippingGroupData(shippingGroup))
  )

  constructor(
    protected checkoutStepService: CheckoutStepService,
    protected activatedRoute: ActivatedRoute,
    protected activeCartFacade: GenActiveCartService,
    protected launchDialogService: LaunchDialogService,
    protected globalMessageService: GlobalMessageService,
    private cdr: ChangeDetectorRef,
    private checkoutService: GenCheckoutService,
  ) {
  }

  private setShippingGroupData(shippingGroup: GenShippingGroup): void {
    this.ltlItemExists(shippingGroup);
    this.shippingGroupName = shippingGroup?.shippingGroupName;
    this.generacShippingAccounts = shippingGroup?.generacShippingAccounts;
    this.dealerShippingAccounts = shippingGroup?.dealerShippingAccounts;
    this.setSelectedDeliveryOption();
    this.loading = GenLoadingEnum.Loaded;
  }

  private setSelectedDeliveryOption(): void {
    let selectedDeliveryOption: GenShippingAccount;
    [this.generacShippingAccounts, this.dealerShippingAccounts].forEach(carrier => {
      if (carrier) {
        carrier.forEach(shippingAccount => {
          const selectedShippingCondition = shippingAccount.shippingConditions?.find(sc => sc?.selected);
          if (selectedShippingCondition) {
            selectedDeliveryOption = {
              ...shippingAccount,
              shippingConditions: [selectedShippingCondition]
            }
          }
        })
      }
    });
    this.selectedCarrier = selectedDeliveryOption?.vendorId || this.generacShippingAccounts[0].vendorId;
    this.selectedShippingCondition = selectedDeliveryOption?.shippingConditions[0].shippingConditionID ||
      this.generacShippingAccounts[0].shippingConditions[0].shippingConditionID;
    if (selectedDeliveryOption) {
      this.selectedShippingAccount = !!selectedDeliveryOption?.generacShippingAccount ?
        GenShippingAccountType.GENERAC : GenShippingAccountType.DEALER;
    }
  }


  private ltlItemExists(shippingGroup: GenShippingGroup) {
    this.isLtlItemExists = shippingGroup?.manufactureGroups?.some(mg =>
      mg?.entryGroups?.some(eg => eg?.entries?.some(e => e?.product?.ltl))
    );
  }

  private openOutOfStockDialog(text: string) {
    const dialog = this.launchDialogService.openDialog(
      'CHECKOUT_OUT_OF_STOCK',
      undefined,
      undefined,
      { text }
    );

    if (dialog) {
      dialog.pipe(take(1), takeUntil(this.destroy$)).subscribe();
    }

    this.launchDialogService.dialogClose
      .pipe(
        takeUntil(this.destroy$),
        tap((reason) => {
          if (reason == 'confirmOutOfStock') {
            this.setDeliveryOptions(true);
          } else if (reason == 'Close Checkout Out Of Stock Dialog') {
            this.selectedShippingCondition = null;
          }
        })
      )
      .subscribe()
  }

  private setDeliveryOptions(confirmOutOfStock: boolean = false) {
    this.loading = GenLoadingEnum.Loading;
    this.checkoutService
      .setDeliveryOptions(this.userId, this.cartCode, this.shippingGroupName, this.carrierSelected, confirmOutOfStock)
      .pipe(
        takeUntil(this.destroy$),
        catchError((err: any) => {
          this.loading = GenLoadingEnum.Loaded;
          if (!!err.error?.showOutOfStockWarning) {
            this.openOutOfStockDialog(err.error?.message);
          } else {
            this.selectedShippingCondition = null;
            this.globalMessageService.add(err.error.errors[0].message, GlobalMessageType.MSG_TYPE_ERROR);
          }
          this.cdr.markForCheck();
          return of();
        })
      ).subscribe(() => {
        this.loading = GenLoadingEnum.Loaded;
        this.activeCartFacade.reloadActiveCart();
        this.checkoutStepService.next(this.activatedRoute)
      })
  }

  getDeliveryMethods(shippingAccount: GenShippingAccountType): void {
    this.updateDeliveryMethodsSubject$$.next(shippingAccount);
  }

  setCarrier(carrier: GenShippingAccount): void {
    this.carrierSelected = carrier;
    this.setSelectedDeliveryOption();
  }

  next(): void {
    this.setDeliveryOptions();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);;
    this.destroy$.complete();
  }
}
